import * as assert from 'assert-plus'
import { AxiosInstance } from 'axios'
import classNames from 'classnames'
import dateFormat from 'dateformat'
import React, { useContext, useEffect, useRef, useState } from 'react'

import { Alert } from '@material-ui/lab'
import { Button } from '@material-ui/core'
import { Snackbar } from '@material-ui/core'
import Spinner from 'src/components/Spinner'
import ContentLoader from 'react-content-loader'
import { withRouter } from 'react-router-dom'
import Workbench from 'src/components/Workbench'
import Back from 'src/components/Back';
import { waitUntilReady } from 'src/utils/async'
import { logError } from 'src/utils/Error'
import AppEnv from 'src/utils/appenv'
import ScrollAnimation from 'src/utils/ScrollAnimation'
import validator from 'validator'
import { APIContext } from '../contexts/api'
import { Auth0Context } from '../contexts/auth0'
import { MergedContexts } from '../contexts/merged'
import { WorkspaceContext } from '../contexts/workspace'
import { IInvitationsProps, IInvitationsState } from '../models/views/IInvitation'
import '../styles.css'
import { bindContexts } from 'src/utils/bindContexts'
import { IconButton, Menu, MenuItem, Tooltip } from '@material-ui/core'
import { SupervisorAccount, MoreVert } from '@material-ui/icons'
import useStyles from 'src/assets/css/components/FormStyles'
import { createStyles, withStyles } from '@material-ui/core/styles';

const styles = () => createStyles({
	largeIcon: {
		width: 30,
		height: 30,
	},
	button: {
		'&:focus': {
			border: 0,
		}
	},
});

interface IInvitationsPropsWithStyles extends IInvitationsProps {
	classes: Record<keyof ReturnType<typeof styles>, string>
}

const Invitations = (props: IInvitationsPropsWithStyles): JSX.Element => {
	const context = useContext(MergedContexts)
	const classes = useStyles()
	const isThisMounted = useRef(false)
	const isLoading = useRef(true)
	const [state, setState] = useState<IInvitationsState>({
		ready: false,
		email: { value: '', isValid: true, message: '' },
		submitSuccess: false,
		invitationsList: [],
		message: [],
		inviteActionMessage: ''
	})

	const getInvitationsData = async () => {
		isLoading.current = false
		// wait at least MINIMUM_LOAD, to show the placeholder smoothly on each load of this view
		waitUntilReady(
			parseInt(AppEnv.APP_PLACEHOLDER_MINIMUM_LOAD, 10),
			parseInt(AppEnv.APP_PLACEHOLDER_LOAD_TIMEOUT, 10),
			isThisMounted.current,
			{ isLoading: () => isLoading.current }
		).then(() => {
			if (isThisMounted.current) {
				setState(s => ({ ...s, ready: true }))
			}
		})
		const { workspace, catalogAPI, setInvitationsList } = context
		if (isThisMounted.current && workspace) {
			setState(s => ({ ...s, ready: false }))
			await catalogAPI
				.get(`${workspace._links.invitations.href}?size=99`, { errorHandler: false })
				.then((resp: any) => {
					if (isThisMounted.current) {
						if (resp.data._embedded) {

							const invitations = resp.data._embedded.invitations.map((invitation: any) => ({
								...invitation,
								isAdmin: invitation._links && invitation._links.hasOwnProperty('withdraw administrator'),
								isMenuOpen: false
							}))

							setState(s => ({
								...s,
								invitationsList: invitations
							}))
							isLoading.current = false
							setInvitationsList(invitations)
						} else {
							setState(s => ({
								...s,
								invitationsList: []
							}))
							isLoading.current = false
							setInvitationsList([])
						}
					}
				})
				.catch((e: any) => {
					if (isThisMounted.current) {
						setState(s => ({
							...s,
							message: ['Error loading invitations. Please try again.']
						}))
						isLoading.current = false
						logError(e)
					}
				})
		}
	}

	useEffect(() => {
		const { catalogAPI } = context
		const api = catalogAPI as AxiosInstance
		assert.func(api, 'APIContext must be initialise before this component is used')
		props.setViewName && props.setViewName("Invitations")

		isThisMounted.current = true
		getInvitationsData()
		return () => {
			isThisMounted.current = false
		}
	}, [])

	const handleInputChanges = (e: any) => {
		e.preventDefault()
		const name = e.currentTarget.name
		const value = e.currentTarget.value
		setState(s => ({
			...s,
			[name]: {
				...s[name],
				value
			}
		}))
	}

	const handleCancelInvitation = async (index: any) => {
		const values = [...state.invitationsList]
		values[index].loading = true

		setState(s => ({ ...s, invitationsList: values }))
		const { catalogAPI } = context
		const api = catalogAPI as AxiosInstance
		const invitation = state.invitationsList[index]
		let cancelInvitationURI = null
		if (invitation._links && invitation._links['withdraw invitation']) {
			cancelInvitationURI = invitation._links['withdraw invitation'].href
		}
		await api
			.put(cancelInvitationURI)
			.then(() => {
				setState(s => ({
					...s,
					inviteActionMessage:
						'Invitation for ' +
						state.invitationsList[index].email +
						' was revoked'
				}))
				values[index].loading = true
				setState(s => ({ ...s, invitationsList: values }))
			})
			.catch(() => {
				setState(s => ({
					...s,
					inviteActionMessage: 'Error while withdrawing invitation. Please try again.'
				}))
				values[index].loading = false
				setState(s => ({ ...s, invitationsList: values }))
			})
		await getInvitationsData()
	}

	const handleResendInvitation = async (index: any) => {
		const values = [...state.invitationsList]
		values[index].loading = true
		setState(s => ({ ...s, invitationsList: values }))

		const { workspace, catalogAPI } = context
		const email = state.invitationsList[index].email
		const errors: any[] = []

		if (errors.length === 0) {
			const InvitationData = { email: email.trim() }
			await catalogAPI
				.post(workspace._links['create invitation'].href, InvitationData, {
					errorHandler: false
				})
				.catch((error: any) => {
					if (
						error &&
						error.response &&
						error.response.data &&
						(error.response.data.message || error.response.data.detail)
					) {
						errors.push(error.response.data.detail || error.response.data.message)
					} else if (error && error.response && error.response.message) {
						errors.push(error.response.message)
					} else {
						errors.push('Unexpected error while resending invitation')
					}
				})
		}
		if (errors.length > 0) {
			setState(s => ({ ...s, ready: true, inviteActionMessage: errors[0] }))
		} else {
			setState(s => ({
				...s,
				ready: true,
				inviteActionMessage: `Invitation for ${email} was revoked and resent`
			}))
		}
		// const afterValues = [...state.invitationsList]
		// afterValues[index].loading = true
		await getInvitationsData()
	}

	const handleDeleteInvitation = async (index: number) => {
		const api = context.catalogAPI as AxiosInstance
		const invitations = [...state.invitationsList]

		await api.delete(invitations[index]._links['delete invitation'].href)
			.catch(logError)

		await getInvitationsData()

	}

	const handleAddAdministrator = async (index: number) => {
		const api = context.catalogAPI as AxiosInstance
		const invitations = [...state.invitationsList]

		await api.put(invitations[index]._links['add administrator'].href)
			.catch(logError)

		await getInvitationsData()

	}

	const handleWithdrawAdministrator = async (index: number) => {

		const api = context.catalogAPI as AxiosInstance
		const invitations = [...state.invitationsList]

		await api.delete(invitations[index]._links['withdraw administrator'].href)
			.catch(logError)

		await getInvitationsData()

	}

	const formIsValid = async () => {
		const email = { ...state.email }
		let isGood = true

		const emailList = email.value.split(',')
		emailList.map(async (emailValue: any) => {
			if (!validator.isEmail(emailValue.trim())) {
				email.isValid = false
				email.message = 'Enter valid email address'
				isGood = false
			}
		})

		// perform addtion validation on password and confirmPassword here...

		if (!isGood) {
			setState(s => ({ ...s, email }))
		}

		return isGood
	};

	const resetValidationStates = () => {
		setState(s => ({
			...s,
			email: { ...s.email, isValid: true, message: '' }
		}))
	}

	const submitHandler = async (e: any) => {
		e.preventDefault()
		// reset states before the validation procedure is run.
		resetValidationStates()
		// run the validation, and if it's good move on.

		if (await formIsValid()) {
			const { workspace, catalogAPI } = context
			const { email } = state

			setState(s => ({
				...s,
				submitSuccess: false,
				message: [],
				ready: false,
				inviteActionMessage: ''
			}))
			isLoading.current = true
			const emailList = email.value.split(',')
			const errors: any[] = []
			await Promise.all(
				emailList.map(async (emailObj: any) => {
					const InvitationData = { email: emailObj.trim() }
					await catalogAPI
						.post(workspace._links['create invitation'].href, InvitationData, {
							errorHandler: false
						})
						.catch((error: any) => {
							if (
								error &&
								error.response &&
								error.response.data &&
								(error.response.data.message || error.response.data.detail)
							) {
								errors.push(error.response.data.detail || error.response.data.message)
							} else if (error && error.response && error.response.message) {
								errors.push(error.response.message)
							} else {
								errors.push('Unexpected error while sending invitation')
							}
						})
				})
			)
			setState(s => ({ ...s, email: { value: '' } }))
			if (errors.length > 0) {
				setState(s => ({ ...s, submitSuccess: false, ready: true, message: errors }))
			} else {
				setState(s => ({
					...s,
					submitSuccess: true,
					ready: true,
					message: ['Invitation sent successfully']
				}))
			}
			if (errors.length < emailList.length) {
				await getInvitationsData()
			}
		}
		return false
	}

	const { email, ready, invitationsList, inviteActionMessage, message } = state
	const uniueMessage = Array.from(new Set(message))
	const emailGroupClass = classNames('form-group', { 'has-error': !email.isValid })

	const { workspace } = context
	const allowedDomainsMsg = workspace.domains.length
		? `You can only invite users from the following domains (${workspace.domains.map((d: string) => `@${d}`).join(", ")})`
		: ''

	const contentLoader = (
		<div className={ready ? 'feed-hidden loaderfadeOut col' : 'feed col'}>
			{/* loaderfadeOut */}
			<div className="dataLoader_parent d-flex" data-testid="loader">
				<div className="dataLoader pad_0 col">
					<ContentLoader className="placeholderList w-100" height="25" speed={2}>
						<rect x="0" y="0" rx="0" ry="0" width="150" height="15" />
					</ContentLoader>

					<ContentLoader
						className="placeholderDivider w-100 d-none d-md-block"
						height={10}
						speed={2}
					>
						<rect x="0" y="5" height="2" />
					</ContentLoader>
					<div className="d-none d-md-block clearfix">
						<div className="clearfix col-md-6 float-left pad_0 align-self-center">
							<ContentLoader
								className="col-md-8 float-left pad_0"
								height="25"
								speed={2}
							>
								<rect x="5" y="15" rx="0" ry="0" width="100" height="8" />
							</ContentLoader>
							<ContentLoader
								className="col-md-4 float-left pad_0"
								height="25"
								speed={2}
							>
								<rect x="5" y="15" rx="0" ry="0" width="100" height="8" />
							</ContentLoader>
						</div>
						<ContentLoader
							className="col-md-2 float-left pad_0 align-self-center status"
							height="35"
							speed={2}
						>
							<rect x="5" y="15" rx="0" ry="0" width="100" height="8" />
						</ContentLoader>
					</div>

					<ContentLoader className="placeholderDivider w-100" height={10} speed={2}>
						<rect x="0" y="5" height="2" />
					</ContentLoader>

					<div className="clearfix">
						<div className="clearfix col-8 col-md-6 float-left pad_0 align-self-center">
							<ContentLoader
								className="col-12 col-md-8 float-left pad_0"
								height="25"
								speed={2}
							>
								<rect x="5" y="15" rx="0" ry="0" width="200" height="8" />
							</ContentLoader>
							<ContentLoader
								className="col-12 col-md-4 float-left pad_0"
								height="25"
								speed={2}
							>
								<rect x="5" y="15" rx="0" ry="0" width="150" height="8" />
							</ContentLoader>
						</div>
						<ContentLoader
							className="col-4 col-md-2 float-left pad_0 align-self-center"
							height="26"
							speed={2}
						>
							<rect x="5" y="15" rx="0" ry="0" width="100" height="8" />
						</ContentLoader>
						<ContentLoader
							className="col-12 col-md-4 float-left pad_0 align-self-center"
							height="35"
							speed={2}
						>
							<rect x="5" y="11" rx="0" ry="0" width="130" height="16" />
							<rect x="145" y="11" rx="0" ry="0" width="100" height="16" />
						</ContentLoader>
					</div>

					<ContentLoader className="placeholderDivider w-100" height={10} speed={2}>
						<rect x="0" y="5" height="2" />
					</ContentLoader>

					<div className="clearfix">
						<div className="clearfix col-8 col-md-6 float-left pad_0 align-self-center">
							<ContentLoader
								className="col-12 col-md-8 float-left pad_0"
								height="25"
								speed={2}
							>
								<rect x="5" y="15" rx="0" ry="0" width="200" height="8" />
							</ContentLoader>
							<ContentLoader
								className="col-12 col-md-4 float-left pad_0"
								height="25"
								speed={2}
							>
								<rect x="5" y="15" rx="0" ry="0" width="150" height="8" />
							</ContentLoader>
						</div>
						<ContentLoader
							className="col-4 col-md-2 float-left pad_0 align-self-center"
							height="26"
							speed={2}
						>
							<rect x="5" y="15" rx="0" ry="0" width="100" height="8" />
						</ContentLoader>
						<ContentLoader
							className="col-12 col-md-4 float-left pad_0 align-self-center"
							height="35"
							speed={2}
						>
							<rect x="5" y="11" rx="0" ry="0" width="130" height="16" />
							<rect x="145" y="11" rx="0" ry="0" width="100" height="16" />
						</ContentLoader>
					</div>
				</div>
			</div>
		</div>
	)

	const handleToastClose = () => {
		setState(s => ({ ...s, submitSuccess: false, message: [] }))
	}
	const handleEmailInput = (e: any) => handleInputChanges(e)

	const sendInvitationsDisabled = !workspace._links['create invitation']

	return (
		<Workbench>
			<div className="w-100" data-testid="resolved">
				<div className="full_pageContent clearfix">
					<Back />
					<div className={classes.root}>
						<Snackbar
							anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
							open={state.submitSuccess}
							autoHideDuration={6000}
							onClose={handleToastClose}>
							<Alert variant='filled' severity='success'>
								{uniueMessage}
							</Alert>
						</Snackbar>
						{state.submitSuccess === false &&
							uniueMessage.length > 0 &&
							uniueMessage.map((msg: any, index: any) => (
								<Alert key={index} severity='warning'>{msg}</Alert>
							))}
						<ScrollAnimation animateIn="fadeIn" offset={10}>
							<h1>Invite others to join your workspace</h1>
						</ScrollAnimation>
						<form onSubmit={submitHandler}>
							<ScrollAnimation animateIn="fadeIn" offset={10}>
								<div
									className={emailGroupClass}
								>
									<small className="text-muted form-text">
										Enter an email address, or comma separate multiple
										addresses. {allowedDomainsMsg}
									</small>
									<small
										className="validation_msg form-text"
										data-testid="email_err"
									>
										{email.message}
									</small>
									<textarea
										data-testid="email"
										rows={3}
										onChange={handleEmailInput}
										value={email.value}
										name="email"
										className="form-control"
									/>
								</div>
							</ScrollAnimation>
							<Tooltip
								arrow
								title={sendInvitationsDisabled ? AppEnv.APP_DISABLED_FEATURE_DEFAULT_TOOLTIP_TITLE : ""}>
								<span>
									<ScrollAnimation animateIn="fadeIn" offset={10}>
									<Button
										variant='contained'
										color="primary"
										disabled={sendInvitationsDisabled || !ready}
										onClick={submitHandler}>
										<Spinner active={!ready}>
											SEND INVITATIONS
										</Spinner>
									</Button>
									</ScrollAnimation>
								</span>
							</Tooltip>
						</form>
					</div>
				</div>
				<div className="full_pageContent pad_tb15 clearfix">
					{inviteActionMessage && (
						<Alert severity='warning'>{inviteActionMessage}</Alert>
					)}
					{ready ? (
						<>
							<ScrollAnimation animateIn="fadeIn" offset={10}>
								<h1>Invitations</h1>
							</ScrollAnimation>
							<ScrollAnimation
								afterAnimatedIn={props.afterInvitationsAnimatedIn}
								animateIn="fadeIn"
								offset={10}
							>
								<div className="inviteList" data-testid="inviteList">
									{invitationsList && invitationsList.length > 0
										? (
											<>
												<div className="inviteListRow rowHead  d-none d-md-flex clearfix">
													<div className="clearfix col-md-7 col-lg-8  col-xl-9 float-left pad_0 align-self-center">
														<div className="col-md-9 float-left pad_0">
															Email address
														</div>
														<div className="col-md-3 float-left pad_0">
															Date
														</div>
													</div>
													<div className="col-md-1  col-lg-1  col-xl-1 float-left pad_0 align-self-center status">
														Status
													</div>
													<div className="col-md-4  col-lg-3  col-xl-2 float-left pad_0" />
												</div>
												{invitationsList.map((row: any, index: any) => {

													const setMenuState = (isOpen: boolean, target?: EventTarget & HTMLButtonElement) => {
														const invitationsList = [...state.invitationsList]
														invitationsList[index].isMenuOpen = isOpen
														if (target) invitationsList[index].anchorEl = target
														setState(s => ({ ...s, invitationsList }))
													}
													const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => setMenuState(true, e.currentTarget)
													const handleCloseMenu = () => setMenuState(false)

													const handleResend = () => { handleCloseMenu(); handleResendInvitation(index) }
													const handleCancel = () => { handleCloseMenu(); handleCancelInvitation(index) }
													const handleDelete = () => { handleCloseMenu(); handleDeleteInvitation(index) }
													const handleMakeAdmin = () => { handleCloseMenu(); handleAddAdministrator(index) }
													const handleRemoveAdmin = () => { handleCloseMenu(); handleWithdrawAdministrator(index) }

													return (
														<div
															className="inviteListRow clearfix"
															key={index}
														>
															<div className="clearfix col-8  col-md-7  col-lg-8  col-xl-9 float-left pad_0 align-self-center">
																<div className="col-12  col-md-9 float-left pad_0 email">
																	{row.email}
																</div>
																<div className="col-12  col-md-3 float-left pad_0 date">
																	{dateFormat(
																		row.created,
																		'mmmm dd, yyyy'
																	)}
																</div>
															</div>
															<div className="col-4  col-md-1  col-lg-1  col-xl-1 float-left pad_0 align-self-center status">
																<span className={row.status.toLowerCase()}>
																	{row.status}
																</span>
															</div>
															<div className="col-12  col-md-4  col-lg-3  col-xl-2 float-left pad_0 align-self-center inviteButtons">
																{row.isAdmin &&
																	<Tooltip
																		title="Administrator"
																		enterDelay={500}>
																		<SupervisorAccount aria-label='admin' />
																	</Tooltip>}
																<IconButton className={props.classes.button}
																	aria-label='actions-menu'
																	onClick={handleOpenMenu}>
																	<Spinner active={row.loading}><MoreVert /></Spinner>
																</IconButton>
																<Menu
																	open={row.isMenuOpen}
																	anchorEl={row.anchorEl}
																	anchorOrigin={{
																		vertical: 'top',
																		horizontal: 'left',
																	}}
																	transformOrigin={{
																		vertical: 'top',
																		horizontal: 'right',
																	}}
																	onClose={handleCloseMenu}>
																	{row.status !== 'ACCEPTED' ? (
																		<MenuItem
																			dense
																			onClick={handleResend}>
																			Resend
																		</MenuItem>
																	) : (
																		<MenuItem
																			dense
																			onClick={!row.isAdmin ? handleMakeAdmin : handleRemoveAdmin}>
																			{!row.isAdmin ? "Make" : "Remove"} admin
																		</MenuItem>
																	)}
																	{row.status !== 'ACCEPTED' && row._links && row._links['delete invitation'] && (
																		<MenuItem
																			dense
																			onClick={handleDelete}>
																			Delete
																		</MenuItem>
																	)}
																	{row.status !== 'REVOKED' &&
																		<MenuItem
																			dense
																			onClick={handleCancel}>
																			Revoke
																		</MenuItem>}
																</Menu>
															</div>
														</div>
													)
												})}
											</>
										)
										: (
											<div className="custom_alertbox">
												<div className="alert text-center">
													You have not sent any invitations for this workspace.
												</div>
											</div>
										)}
								</div>
							</ScrollAnimation>
						</>
					) : contentLoader}
				</div>
			</div>
		</Workbench>
	)
}

export const InvitationsWithContext = bindContexts(Invitations, [
	Auth0Context,
	APIContext,
	WorkspaceContext
])
//export default withRouter(InvitationsWithContext)
export default withStyles(styles)(withRouter(InvitationsWithContext));
